import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeResponse } from "@passa-a-pagina/shared";
import { api, ApiError } from "./api";

export interface SessionUser {
  id: string;
  email: string;
  role: "user" | "admin";
  points: number;
  firstName?: string | null;
  lastName?: string | null;
}

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  refresh: () => Promise<unknown>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const query = useQuery<SessionUser | null, Error>({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      try {
        const res = await api<MeResponse>("/api/auth/me");
        return res.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      user: query.data ?? null,
      loading: query.isPending,
      refresh: () => qc.invalidateQueries({ queryKey: ["auth", "me"] }),
      logout: async () => {
        await api<void>("/api/auth/logout", { method: "POST" });
        qc.setQueryData(["auth", "me"], null);
      },
    }),
    [query.data, query.isPending, qc],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
